import type {
  AgentChanges,
  AgentDetailDto,
  AgentDto,
  AgentVersionDto,
  EndpointDto,
  ProviderDto,
  MemoryMode,
  ReasoningEffort,
  RequestContextValues,
  Role,
  SessionLevel,
  UpdateDraftRequest,
} from '@agent-platform/shared';
import {
  DEFAULT_LIMITS,
  REASONING_EFFORTS,
  sessionConfigSchema,
  sessionVariables,
  hasRole,
  lineDiff,
  promptVariables,
  type PromptBlockDto,
} from '@agent-platform/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import {
  Archive,
  BookOpen,
  ArchiveRestore,
  Brain,
  Check,
  ChevronsUpDown,
  CircleAlert,
  Code2,
  Cpu,
  Ellipsis,
  FileText,
  Gauge,
  History,
  LoaderCircle,
  MessageSquare,
  Pencil,
  Search,
  Server,
  Settings2,
  Sparkles,
  Trash2,
  Variable,
  Workflow,
  Wrench,
} from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { AgentBuilder } from '@/components/agent-builder';
import { AgentChat } from '@/components/agent-chat';
import { levelsSummary, SessionLevelsEditor } from '@/components/session-levels-editor';
import { AgentCode } from '@/components/agent-code';
import { expectedHeaders } from '@/components/agent-headers';
import { ErrorAlert } from '@/components/error-alert';
import { PageHeader } from '@/components/page-header';
import { PromptEditor } from '@/components/prompt-editor';
import { RequestContextPicker } from '@/components/request-context-picker';
import { RequireTenant } from '@/components/require-tenant';
import { useMediaQuery } from '@/hooks/use-media-query';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { api, errorMessage } from '@/lib/api';
import { AGENT_STATUS_LABELS, formatDateTime } from '@/lib/format';
import { usePaged } from '@/lib/queries';
import { cn } from '@/lib/utils';
import { STATUS_VARIANT } from './agents';

const NO_PROVIDER = 'none';
/** The effort select's "not set" value (the provider default). */
const DEFAULT_EFFORT = 'default';
const EFFORT_LABELS: Record<ReasoningEffort, string> = {
  none: 'None',
  minimal: 'Minimal',
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  xhigh: 'Extra high',
  max: 'Max',
};

/** Form state: numbers stay strings while typing; blank means "not set". */
interface DraftForm {
  systemPrompt: string;
  providerConnectionId: string | null;
  model: string;
  temperature: string;
  topP: string;
  maxTokens: string;
  effort: ReasoningEffort | null;
  promptCaching: boolean;
  maxSteps: string;
  maxDurationSeconds: string;
  maxOutputTokens: string;
  toolIds: string[];
  mcpServerIds: string[];
  skillIds: string[];
  workspaceIds: string[];
  workflowIds: string[];
  claudeBuiltinTools: string[];
  webSearch: boolean;
  memoryMode: MemoryMode;
  sessionLevels: SessionLevel[];
  lastMessages: string;
}

const str = (n: number | undefined) => (n === undefined ? '' : String(n));

function fromVersion(v: AgentVersionDto): DraftForm {
  return {
    systemPrompt: v.systemPrompt,
    providerConnectionId: v.providerConnectionId,
    model: v.model ?? '',
    temperature: str(v.params.temperature),
    topP: str(v.params.topP),
    maxTokens: str(v.params.maxTokens),
    effort: v.params.effort ?? null,
    promptCaching: v.params.promptCaching !== false,
    maxSteps: str(v.limits.maxSteps),
    maxDurationSeconds: str(v.limits.maxDurationMs / 1000),
    maxOutputTokens: str(v.limits.maxOutputTokens),
    toolIds: v.toolIds,
    mcpServerIds: v.mcpServerIds,
    skillIds: v.skillIds,
    workspaceIds: v.workspaceIds,
    workflowIds: v.workflowIds,
    claudeBuiltinTools: v.claudeBuiltinTools,
    webSearch: v.webSearch,
    memoryMode: v.memoryMode,
    sessionLevels: v.sessionConfig.levels,
    lastMessages: str(v.memoryConfig.lastMessages),
  };
}

const num = (s: string) => (s.trim() === '' ? undefined : Number(s));

function toRequest(f: DraftForm): UpdateDraftRequest {
  const params = {
    temperature: num(f.temperature),
    topP: num(f.topP),
    maxTokens: num(f.maxTokens),
    effort: f.effort ?? undefined,
    // Only the off state is stored; on is the default.
    promptCaching: f.promptCaching ? undefined : false,
  };
  const maxDuration = num(f.maxDurationSeconds);
  return {
    systemPrompt: f.systemPrompt,
    providerConnectionId: f.providerConnectionId,
    model: f.model.trim() || null,
    params: Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined)),
    limits: {
      maxSteps: num(f.maxSteps) ?? DEFAULT_LIMITS.maxSteps,
      maxDurationMs:
        maxDuration === undefined ? DEFAULT_LIMITS.maxDurationMs : Math.round(maxDuration * 1000),
      ...(num(f.maxOutputTokens) !== undefined ? { maxOutputTokens: num(f.maxOutputTokens) } : {}),
    },
    toolIds: [...f.toolIds].sort(),
    mcpServerIds: [...f.mcpServerIds].sort(),
    skillIds: [...f.skillIds].sort(),
    workspaceIds: [...f.workspaceIds].sort(),
    workflowIds: [...f.workflowIds].sort(),
    claudeBuiltinTools: [
      ...f.claudeBuiltinTools,
    ].sort() as UpdateDraftRequest['claudeBuiltinTools'],
    webSearch: f.webSearch,
    memoryMode: f.memoryMode,
    // Saved only when valid; the editor shows what to fix meanwhile.
    ...(sessionConfigSchema.safeParse({ levels: f.sessionLevels }).success
      ? { sessionConfig: { levels: f.sessionLevels } }
      : {}),
    memoryConfig: { lastMessages: num(f.lastMessages) ?? 20 },
  };
}

const FIELD_LABELS = {
  systemPrompt: 'System prompt',
  providerConnectionId: 'Provider',
  model: 'Model',
  params: 'Model parameters',
  limits: 'Limits',
  toolIds: 'Tools',
  mcpServerIds: 'MCP servers',
  skillIds: 'Skills',
  workspaceIds: 'Workspaces',
  workflowIds: 'Workflows',
  claudeBuiltinTools: 'Claude built-in tools',
  webSearch: 'Web search',
  memoryMode: 'Memory',
  sessionConfig: 'Session levels',
  memoryConfig: 'Memory settings',
} as const;

function changedFields(a: AgentVersionDto | null, b: AgentVersionDto): string[] {
  if (!a) return ['First version'];
  return (Object.keys(FIELD_LABELS) as Array<keyof typeof FIELD_LABELS>)
    .filter((f) => JSON.stringify(a[f]) !== JSON.stringify(b[f]))
    .map((f) => FIELD_LABELS[f]);
}

/** The system prompt change about to be published, as a line diff. */
function PromptDiff({ before, after }: { before: string; after: string }) {
  const lines = lineDiff(before, after);
  const box = useRef<HTMLPreElement>(null);
  // Open at the first change, not at the top of an unchanged prompt.
  useEffect(() => {
    const first = box.current?.querySelector<HTMLElement>('[data-change]');
    if (box.current && first) box.current.scrollTop = Math.max(0, first.offsetTop - 48);
  }, []);
  const added = lines.filter((l) => l.kind === 'added').length;
  const removed = lines.filter((l) => l.kind === 'removed').length;
  return (
    <div className="grid min-w-0 gap-1.5">
      <span className="flex items-baseline gap-3 text-sm font-medium">
        System prompt changes
        <span className="text-muted-foreground text-xs font-normal">
          {added} added, {removed} removed
        </span>
      </span>
      <pre
        ref={box}
        className="bg-muted relative max-h-[45vh] overflow-auto rounded-md border p-3 font-mono text-xs leading-5 whitespace-pre-wrap [overflow-wrap:anywhere]"
        aria-label="System prompt changes"
        data-testid="prompt-diff"
      >
        {lines.map((line, i) => (
          <div
            key={i}
            data-change={line.kind === 'same' ? undefined : ''}
            className={
              line.kind === 'added'
                ? 'bg-green-500/15 text-green-800 dark:text-green-300'
                : line.kind === 'removed'
                  ? 'bg-red-500/15 text-red-800 line-through dark:text-red-300'
                  : 'text-muted-foreground'
            }
          >
            {line.kind === 'added' ? '+ ' : line.kind === 'removed' ? '- ' : '  '}
            {line.text || ' '}
          </div>
        ))}
      </pre>
    </div>
  );
}

type SaveState = 'saved' | 'pending' | 'saving' | 'error';

/** Inserts a prompt from the library at the cursor (a copy: published versions stay immutable). */
function InsertPromptMenu({
  tenantId,
  disabled,
  onInsert,
}: {
  tenantId: string;
  disabled: boolean;
  onInsert: (content: string) => void;
}) {
  const prompts = useQuery({
    queryKey: ['prompts', tenantId],
    queryFn: () => api<{ items: PromptBlockDto[] }>('/prompts'),
  });
  const items = prompts.data?.items ?? [];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant="outline" disabled={disabled}>
          Insert from library
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel>Prompt library</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {items.length === 0 ? (
          <DropdownMenuItem asChild>
            <Link to="/prompts">No prompts yet. Create one</Link>
          </DropdownMenuItem>
        ) : (
          items.map((p) => (
            <DropdownMenuItem key={p.id} onSelect={() => onInsert(p.content)}>
              <div className="grid min-w-0">
                <span className="truncate font-mono text-xs">{p.name}</span>
                {p.description && (
                  <span className="text-muted-foreground truncate text-xs">{p.description}</span>
                )}
              </div>
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface NamedItem {
  id: string;
  name: string;
  description: string;
}

function useNamed(tenantId: string, path: string) {
  return useQuery({
    queryKey: [path.slice(1), tenantId],
    queryFn: () => api<{ items: NamedItem[] }>(path),
  });
}

/** A searchable list of attachable resources (tools, MCP servers or skills). */
function AttachmentGroup({
  tenantId,
  label,
  path,
  manage,
  selected,
  onChange,
  disabled,
}: {
  tenantId: string;
  label: string;
  path: string;
  manage: '/tools' | '/skills' | '/workflows' | '/workspaces';
  selected: string[];
  onChange: (ids: string[]) => void;
  disabled: boolean;
}) {
  const [query, setQuery] = useState('');
  const items = useNamed(tenantId, path);
  const list = items.data?.items ?? [];
  const q = query.trim().toLowerCase();
  const visible = q
    ? list.filter((i) => `${i.name} ${i.description}`.toLowerCase().includes(q))
    : list;
  return (
    <fieldset className="grid content-start gap-3">
      <div className="flex items-baseline justify-between gap-3">
        <legend className="text-sm font-medium">{label}</legend>
        <span className="text-muted-foreground text-xs">
          {selected.length} of {list.length} attached ·{' '}
          <Link to={manage} className="underline">
            Manage
          </Link>
        </span>
      </div>
      {list.length > 6 && (
        <div className="relative">
          <Search
            className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2"
            aria-hidden
          />
          <Input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`Filter ${label.toLowerCase()}`}
            aria-label={`Filter ${label.toLowerCase()}`}
            className="pl-9"
          />
        </div>
      )}
      {items.isPending && <p className="text-muted-foreground text-sm">Loading…</p>}
      <ErrorAlert error={items.error} />
      {!items.isPending && list.length === 0 && (
        <p className="text-muted-foreground rounded-xl border border-dashed p-4 text-sm">
          No {label.toLowerCase()} yet.{' '}
          <Link to={manage} className="underline">
            Create one
          </Link>{' '}
          to attach it here.
        </p>
      )}
      {list.length > 0 && visible.length === 0 && (
        <p className="text-muted-foreground text-sm">Nothing matches “{query}”.</p>
      )}
      <ul className="grid max-h-[50vh] gap-1 overflow-y-auto">
        {visible.map((item) => (
          <li key={item.id}>
            <label className="has-[:checked]:bg-accent has-[:focus-visible]:ring-ring/50 hover:bg-muted/60 flex cursor-pointer items-start gap-3 rounded-[10px] px-3 py-2 has-[:disabled]:cursor-not-allowed has-[:focus-visible]:ring-3">
              <input
                type="checkbox"
                className="accent-primary mt-1"
                disabled={disabled}
                checked={selected.includes(item.id)}
                onChange={(e) =>
                  onChange(
                    e.target.checked
                      ? [...selected, item.id]
                      : selected.filter((x) => x !== item.id),
                  )
                }
              />
              <span className="grid min-w-0 gap-0.5">
                <span className="truncate font-mono text-sm">{item.name}</span>
                {item.description && (
                  <span className="text-muted-foreground line-clamp-2 text-xs">
                    {item.description}
                  </span>
                )}
              </span>
            </label>
          </li>
        ))}
      </ul>
    </fieldset>
  );
}

function BuiltinTools({
  tenantId,
  disabled,
  selected,
  onChange,
}: {
  tenantId: string;
  disabled: boolean;
  selected: string[];
  onChange: (tools: string[]) => void;
}) {
  const caps = useQuery({
    queryKey: ['tool-capabilities', tenantId],
    queryFn: () => api<{ claudeBuiltinToolsAllowed: string[] }>('/tools/capabilities'),
  });
  const allowed = caps.data?.claudeBuiltinToolsAllowed ?? [];
  return (
    <fieldset className="grid gap-3">
      <legend className="text-sm font-medium">Claude Code built-in tools</legend>
      <p className="text-muted-foreground text-xs">
        These run on the API server. Only tools the operator lists in{' '}
        <code>CLAUDE_BUILTIN_TOOLS_ALLOWED</code> can be turned on.
      </p>
      <div className="flex flex-wrap gap-2">
        {allowed.length === 0 && (
          <p className="text-muted-foreground text-sm">None are allowed on this server.</p>
        )}
        {allowed.map((t) => (
          <label
            key={t}
            className="has-[:checked]:bg-accent flex cursor-pointer items-center gap-2 rounded-full border px-3 py-1 font-mono text-sm"
          >
            <input
              type="checkbox"
              className="accent-primary"
              disabled={disabled}
              checked={selected.includes(t)}
              onChange={(e) =>
                onChange(e.target.checked ? [...selected, t] : selected.filter((x) => x !== t))
              }
            />
            {t}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function WebSearchToggle({
  disabled,
  checked,
  onChange,
}: {
  disabled: boolean;
  checked: boolean;
  onChange: (on: boolean) => void;
}) {
  return (
    <fieldset className="grid gap-2">
      <legend className="text-sm font-medium">Web search</legend>
      <label className="flex cursor-pointer items-center gap-2 text-sm">
        <input
          type="checkbox"
          className="accent-primary"
          disabled={disabled}
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
        />
        Let this agent search the web and read pages (<code>web_search</code>,{' '}
        <code>web_fetch</code>)
      </label>
    </fieldset>
  );
}

function VersionsList({ tenantId, agent }: { tenantId: string; agent: AgentDetailDto }) {
  const versions = useQuery({
    queryKey: ['agent-versions', tenantId, agent.id, agent.publishedVersionId],
    queryFn: () => api<{ items: AgentVersionDto[] }>(`/agents/${agent.id}/versions`),
  });
  const [open, setOpen] = useState<string | null>(null);
  const items = versions.data?.items ?? [];
  return (
    <div className="mx-auto grid w-full max-w-4xl gap-2">
      <p className="text-muted-foreground mb-2 text-sm">
        Published versions never change. Your edits always go to the draft.
      </p>
      <ErrorAlert error={versions.error} />
      {versions.isPending && <p className="text-muted-foreground text-sm">Loading…</p>}
      <ol className="grid gap-2">
        {items.map((v, i) => {
          const previous = items.slice(i + 1).find((p) => p.frozen) ?? null;
          const isOpen = open === v.id;
          const live = v.id === agent.publishedVersionId;
          return (
            <li key={v.id} className="bg-card shadow-panel rounded-xl text-sm">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3">
                <span className="font-semibold tabular-nums">v{v.version}</span>
                {!v.frozen && <Badge variant="outline">Draft</Badge>}
                {live && <Badge>Live</Badge>}
                <span className="text-muted-foreground font-mono text-xs">
                  {v.model ?? 'no model'}
                </span>
                <span className="text-muted-foreground ml-auto text-xs">
                  {v.publishedAt
                    ? `Published ${formatDateTime(v.publishedAt)}`
                    : `Edited ${formatDateTime(v.updatedAt)}`}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-expanded={isOpen}
                  onClick={() => setOpen(isOpen ? null : v.id)}
                >
                  {isOpen ? 'Hide' : 'View'}
                </Button>
              </div>
              <p className="text-muted-foreground border-t px-4 py-2 text-xs">
                {previous || v.frozen
                  ? `Changed: ${changedFields(previous, v).join(', ') || 'nothing'}`
                  : 'Not published yet'}
              </p>
              {isOpen && (
                <div className="grid gap-2 border-t px-4 py-3">
                  <pre className="bg-muted max-h-80 overflow-auto rounded-lg p-3 font-mono text-xs leading-5 whitespace-pre-wrap">
                    {v.systemPrompt || '(empty system prompt)'}
                  </pre>
                  <pre className="bg-muted overflow-auto rounded-lg p-3 font-mono text-xs">
                    {JSON.stringify(
                      { params: v.params, limits: v.limits, memory: v.memoryMode },
                      null,
                      2,
                    )}
                  </pre>
                </div>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

// --- Config panel ---

/** One block of the config panel: an icon, a title, an optional count and an action. */
const TINTS = {
  amber: 'text-tint-amber',
  orange: 'text-tint-orange',
  green: 'text-tint-green',
  blue: 'text-tint-blue',
  violet: 'text-tint-violet',
  rose: 'text-tint-rose',
} as const;

function ConfigSection({
  icon: Icon,
  tint,
  title,
  count,
  action,
  children,
}: {
  icon: typeof Wrench;
  tint: keyof typeof TINTS;
  title: string;
  count?: number;
  action?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <section className="grid gap-2.5 px-4 py-3.5">
      <div className="flex min-h-7 items-center gap-2">
        <Icon className={cn('size-4 shrink-0', TINTS[tint])} aria-hidden />
        <h3 className="text-sm font-medium">{title}</h3>
        {count !== undefined && (
          <span className="text-muted-foreground text-xs tabular-nums">{count}</span>
        )}
        <div className="ml-auto flex items-center gap-1">{action}</div>
      </div>
      {children}
    </section>
  );
}

function Chips({ names, empty = 'None' }: { names: string[]; empty?: string }) {
  if (names.length === 0) return <p className="text-muted-foreground text-sm">{empty}</p>;
  return (
    <ul className="flex flex-wrap gap-1.5">
      {names.map((n) => (
        <li
          key={n}
          className="bg-accent text-muted-foreground dark:text-foreground flex h-[22px] items-center rounded-[7px] px-2 text-xs font-medium"
        >
          {n}
        </li>
      ))}
    </ul>
  );
}

/** A label and a compact control on one row. */
function Row({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor?: string;
  children: ReactNode;
}) {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_8.5rem] items-center gap-3">
      <Label htmlFor={htmlFor} className="text-muted-foreground font-normal">
        {label}
      </Label>
      {children}
    </div>
  );
}

const SMALL_INPUT = 'h-7 text-right tabular-nums';

function EditButton({
  label,
  onClick,
  disabled,
}: {
  label: string;
  onClick: () => void;
  disabled: boolean;
}) {
  return (
    <Button size="xs" variant="ghost" onClick={onClick} disabled={disabled} aria-label={label}>
      <Pencil aria-hidden />
      Edit
    </Button>
  );
}

type EditorDialog = 'prompt' | 'tools' | 'skills' | 'workspaces' | 'workflows' | 'sessions' | null;

function DraftWorkspace({
  tenantId,
  agent,
  canEdit,
  onSaveState,
}: {
  tenantId: string;
  agent: AgentDetailDto;
  canEdit: boolean;
  onSaveState: (state: SaveState) => void;
}) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<DraftForm>(() => fromVersion(agent.draft));
  const [tab, setTab] = useState<'chat' | 'config' | 'versions' | 'code'>('chat');
  // Build: an assistant proposes changes to this agent. Test: talk to the agent itself.
  const [mode, setMode] = useState<'build' | 'test'>(() =>
    agent.draft.systemPrompt.trim() ? 'test' : 'build',
  );
  // Wide screens show the config beside the chat; narrow ones get it as a tab instead.
  const wide = useMediaQuery('(min-width: 1024px)');
  const view = wide && tab === 'config' ? 'chat' : tab;
  const [dialog, setDialog] = useState<EditorDialog>(null);
  const [requestContext, setRequestContext] = useState<RequestContextValues | null>({});
  const savedKey = useRef(JSON.stringify(toRequest(fromVersion(agent.draft))));
  const providers = usePaged<ProviderDto>(['providers', tenantId], '/providers');
  const providerList = providers.data?.pages.flatMap((p) => p.items) ?? [];
  const provider = providerList.find((p) => p.id === form.providerConnectionId);
  const tools = useNamed(tenantId, '/tools');
  const servers = useNamed(tenantId, '/mcp-servers');
  const skills = useNamed(tenantId, '/skills');
  const workflows = useNamed(tenantId, '/workflows');
  const workspaces = useNamed(tenantId, '/workspaces');
  const capabilities = useQuery({
    queryKey: ['tool-capabilities', tenantId],
    queryFn: () => api<{ claudeBuiltinToolsAllowed: string[] }>('/tools/capabilities'),
  });
  const builtinsAllowed = capabilities.data?.claudeBuiltinToolsAllowed ?? [];
  const endpoints = usePaged<EndpointDto>(['endpoints', tenantId], '/endpoints');
  const endpoint = endpoints.data?.pages
    .flatMap((p) => p.items)
    .find((e) => e.agentId === agent.id);
  const expected = expectedHeaders(endpoint);
  const createEndpoint = useMutation({
    mutationFn: () =>
      api<EndpointDto>('/endpoints', { method: 'POST', body: { agentId: agent.id } }),
    onSuccess: () => {
      toast.success('Endpoint created');
      void queryClient.invalidateQueries({ queryKey: ['endpoints', tenantId] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });
  /**
   * Applies a builder proposal to the form (it then auto-saves like any edit). Attachments are
   * matched by name; names that don't exist are skipped and returned.
   */
  const applyChanges = (c: AgentChanges): string[] => {
    const lookup = (q: typeof tools) => {
      const items = q.data?.items ?? [];
      return (name: string) => items.find((i) => i.name === name)?.id;
    };
    const providerId = c.provider ? providerList.find((p) => p.name === c.provider)?.id : undefined;
    const skipped = [
      ...new Set([
        ...(
          [
            [tools, c.tools],
            [servers, c.mcpServers],
            [skills, c.skills],
            [workflows, c.workflows],
          ] as const
        ).flatMap(([q, change]) => (change?.add ?? []).filter((n) => !lookup(q)(n))),
        // Built-in tools the server does not allow can't be turned on.
        ...(c.claudeBuiltinTools?.add ?? []).filter((n) => !builtinsAllowed.includes(n)),
        ...(c.provider && !providerId ? [c.provider] : []),
      ]),
    ];
    const resolve = (
      q: typeof tools,
      current: string[],
      change: { add: string[]; remove: string[] } | undefined,
    ) => {
      if (!change) return current;
      const idOf = lookup(q);
      const removed = new Set(change.remove.map(idOf).filter(Boolean));
      const added = change.add.map(idOf).filter((id): id is string => Boolean(id));
      return [...new Set([...current.filter((id) => !removed.has(id)), ...added])];
    };
    // Built-in tools are names already; only the ones this server allows can be turned on.
    const builtins = (current: string[], change: typeof c.claudeBuiltinTools) =>
      change
        ? [
            ...new Set([
              ...current.filter((t) => !change.remove.includes(t)),
              ...change.add.filter((t) => builtinsAllowed.includes(t)),
            ]),
          ]
        : current;
    const n = (v: number | undefined, fallback: string) => (v === undefined ? fallback : String(v));
    setForm((f) => ({
      ...f,
      systemPrompt: c.systemPrompt ?? f.systemPrompt,
      providerConnectionId: providerId ?? f.providerConnectionId,
      model: c.model ?? f.model,
      toolIds: resolve(tools, f.toolIds, c.tools),
      mcpServerIds: resolve(servers, f.mcpServerIds, c.mcpServers),
      skillIds: resolve(skills, f.skillIds, c.skills),
      workflowIds: resolve(workflows, f.workflowIds, c.workflows),
      claudeBuiltinTools: builtins(f.claudeBuiltinTools, c.claudeBuiltinTools),
      memoryMode: c.memory?.mode ?? f.memoryMode,
      lastMessages: n(c.memory?.lastMessages, f.lastMessages),
      sessionLevels: c.memory?.sessionLevels ?? f.sessionLevels,
      maxSteps: n(c.limits?.maxSteps, f.maxSteps),
      maxDurationSeconds: n(c.limits?.maxDurationSeconds, f.maxDurationSeconds),
      maxOutputTokens: n(c.limits?.maxOutputTokens, f.maxOutputTokens),
      temperature: n(c.params?.temperature, f.temperature),
      topP: n(c.params?.topP, f.topP),
      maxTokens: n(c.params?.maxTokens, f.maxTokens),
      effort: c.params?.effort ?? f.effort,
    }));
    if (skipped.length === 0) toast.success('Applied to the draft');
    else toast.warning(`Applied. Skipped ${skipped.join(', ')}: not found`);
    return skipped;
  };
  const names = (q: typeof tools, ids: string[]) =>
    (q.data?.items ?? []).filter((i) => ids.includes(i.id)).map((i) => i.name);

  const save = useMutation({
    mutationFn: (body: UpdateDraftRequest) =>
      api<AgentVersionDto>(`/agents/${agent.id}/draft`, { method: 'PATCH', body }),
    onSuccess: (draft, body) => {
      savedKey.current = JSON.stringify(body);
      queryClient.setQueryData<AgentDetailDto>(['agent', tenantId, agent.id], (old) =>
        old ? { ...old, draft } : old,
      );
    },
  });

  const requestKey = JSON.stringify(toRequest(form));
  const dirty = requestKey !== savedKey.current;
  const saveState: SaveState = save.isPending
    ? 'saving'
    : dirty
      ? save.isError
        ? 'error'
        : 'pending'
      : 'saved';

  useEffect(() => {
    onSaveState(saveState);
  }, [saveState, onSaveState]);

  // Auto-save: debounce edits, then PATCH only when the request would change.
  useEffect(() => {
    if (!canEdit || !dirty || save.isPending) return;
    const timer = setTimeout(() => save.mutate(JSON.parse(requestKey) as UpdateDraftRequest), 700);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-run when the request or save status changes
  }, [requestKey, canEdit, save.isPending]);

  const set =
    <K extends keyof DraftForm>(key: K) =>
    (value: DraftForm[K]) =>
      setForm((f) => ({ ...f, [key]: value }));
  const disabled = !canEdit;
  const promptRef = useRef<HTMLTextAreaElement>(null);
  const insertAtCursor = (text: string) => {
    const el = promptRef.current;
    const at = el ? el.selectionStart : form.systemPrompt.length;
    const end = el ? el.selectionEnd : at;
    const before = form.systemPrompt.slice(0, at);
    const sep = before && !before.endsWith('\n\n') ? (before.endsWith('\n') ? '\n' : '\n\n') : '';
    set('systemPrompt')(`${before}${sep}${text}${form.systemPrompt.slice(end)}`);
  };
  const variables = promptVariables(form.systemPrompt);
  // What the test chat lets you fill: prompt variables and session level variables.
  const testVariables = [
    ...new Set([
      ...variables,
      ...(form.memoryMode === 'sessions' ? sessionVariables(form.sessionLevels) : []),
    ]),
  ];
  const changes = changedFields(agent.published, agent.draft);

  const config = (
    <div className="divide-y">
      <ConfigSection icon={Cpu} tint="orange" title="Model">
        <div className="grid gap-2">
          <Select
            value={form.providerConnectionId ?? NO_PROVIDER}
            disabled={disabled}
            onValueChange={(v) => set('providerConnectionId')(v === NO_PROVIDER ? null : v)}
          >
            <SelectTrigger aria-label="Provider" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_PROVIDER}>No provider</SelectItem>
              {providerList.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Label htmlFor="agent-model" className="sr-only">
            Model id
          </Label>
          <Input
            id="agent-model"
            list="agent-model-options"
            autoComplete="off"
            className="font-mono placeholder:font-sans"
            placeholder={provider?.defaultModel ?? 'Model id, e.g. claude-sonnet-5-5'}
            value={form.model}
            disabled={disabled}
            onChange={(e) => set('model')(e.target.value)}
          />
          <datalist id="agent-model-options">
            {provider?.models.map((m) => (
              <option key={m} value={m} />
            ))}
          </datalist>
          {(!form.providerConnectionId || !form.model.trim()) && (
            <p className="text-destructive flex items-center gap-1.5 text-xs">
              <CircleAlert className="size-3.5" aria-hidden />
              Choose a provider and model before publishing.
            </p>
          )}
          <details className="group">
            <summary className="text-muted-foreground hover:text-foreground cursor-pointer list-none text-xs">
              <span className="group-open:hidden">Show model parameters</span>
              <span className="hidden group-open:inline">Hide model parameters</span>
            </summary>
            <div className="mt-2.5 grid gap-2">
              <Row label="Temperature" htmlFor="agent-temperature">
                <Input
                  id="agent-temperature"
                  type="number"
                  step="0.1"
                  placeholder="Default"
                  className={SMALL_INPUT}
                  value={form.temperature}
                  disabled={disabled}
                  onChange={(e) => set('temperature')(e.target.value)}
                />
              </Row>
              <Row label="Top P" htmlFor="agent-top-p">
                <Input
                  id="agent-top-p"
                  type="number"
                  step="0.05"
                  placeholder="Default"
                  className={SMALL_INPUT}
                  value={form.topP}
                  disabled={disabled}
                  onChange={(e) => set('topP')(e.target.value)}
                />
              </Row>
              <Row label="Max tokens per reply" htmlFor="agent-max-tokens">
                <Input
                  id="agent-max-tokens"
                  type="number"
                  placeholder="Default"
                  className={SMALL_INPUT}
                  value={form.maxTokens}
                  disabled={disabled}
                  onChange={(e) => set('maxTokens')(e.target.value)}
                />
              </Row>
              <Row label="Prompt caching">
                <Select
                  value={form.promptCaching ? 'on' : 'off'}
                  disabled={disabled}
                  onValueChange={(v) => set('promptCaching')(v === 'on')}
                >
                  <SelectTrigger aria-label="Prompt caching" size="sm" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="on">On</SelectItem>
                    <SelectItem value="off">Off</SelectItem>
                  </SelectContent>
                </Select>
              </Row>
              <Row label="Reasoning effort">
                <Select
                  value={form.effort ?? DEFAULT_EFFORT}
                  disabled={disabled}
                  onValueChange={(v) =>
                    set('effort')(v === DEFAULT_EFFORT ? null : (v as ReasoningEffort))
                  }
                >
                  <SelectTrigger aria-label="Reasoning effort" size="sm" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={DEFAULT_EFFORT}>Default</SelectItem>
                    {REASONING_EFFORTS.map((e) => (
                      <SelectItem key={e} value={e}>
                        {EFFORT_LABELS[e]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Row>
            </div>
          </details>
        </div>
      </ConfigSection>

      <ConfigSection
        icon={FileText}
        tint="rose"
        title="System prompt"
        action={
          <EditButton
            label="Edit system prompt"
            disabled={disabled}
            onClick={() => setDialog('prompt')}
          />
        }
      >
        <button
          type="button"
          onClick={() => setDialog('prompt')}
          disabled={disabled}
          // A larger click target for the Edit button beside the title, which is the accessible way in.
          aria-hidden
          tabIndex={-1}
          className="bg-muted/70 hover:bg-muted rounded-lg p-3 text-left font-mono text-xs leading-5 disabled:cursor-default"
        >
          {form.systemPrompt.trim() ? (
            <span className="line-clamp-6 whitespace-pre-wrap [overflow-wrap:anywhere]">
              {form.systemPrompt}
            </span>
          ) : (
            <span className="text-muted-foreground font-sans">
              No instructions yet. Click to write them.
            </span>
          )}
        </button>
        <p className="text-muted-foreground text-xs">
          {form.systemPrompt.split('\n').length} lines, about{' '}
          {Math.ceil(form.systemPrompt.length / 4)} tokens
          {variables.length > 0 &&
            `, ${variables.length} variable${variables.length === 1 ? '' : 's'}`}
        </p>
      </ConfigSection>

      {testVariables.length > 0 && (
        <ConfigSection icon={Variable} tint="blue" title="Test values">
          <p className="text-muted-foreground text-xs">
            Values for {testVariables.map((v) => `{{${v}}}`).join(', ')} while you chat here.
            Clients send them with each request (session variables pick the sessions).
          </p>
          <RequestContextPicker
            tenantId={tenantId}
            id="agent-chat-context"
            expected={testVariables}
            onChange={setRequestContext}
          />
        </ConfigSection>
      )}

      <ConfigSection
        icon={Wrench}
        tint="amber"
        title="Tools"
        count={form.toolIds.length}
        action={
          <EditButton label="Edit tools" disabled={disabled} onClick={() => setDialog('tools')} />
        }
      >
        <Chips
          names={[
            ...names(tools, form.toolIds),
            ...form.claudeBuiltinTools,
            ...(form.webSearch ? ['web_search', 'web_fetch'] : []),
          ]}
        />
      </ConfigSection>

      <ConfigSection
        icon={Server}
        tint="green"
        title="MCP servers"
        count={form.mcpServerIds.length}
        action={
          <EditButton
            label="Edit MCP servers"
            disabled={disabled}
            onClick={() => setDialog('tools')}
          />
        }
      >
        <Chips names={names(servers, form.mcpServerIds)} />
      </ConfigSection>

      <ConfigSection
        icon={Sparkles}
        tint="violet"
        title="Skills"
        count={form.skillIds.length}
        action={
          <EditButton label="Edit skills" disabled={disabled} onClick={() => setDialog('skills')} />
        }
      >
        <Chips names={names(skills, form.skillIds)} />
      </ConfigSection>

      <ConfigSection
        icon={BookOpen}
        tint="blue"
        title="Knowledge"
        count={form.workspaceIds.length}
        action={
          <EditButton
            label="Edit knowledge workspaces"
            disabled={disabled}
            onClick={() => setDialog('workspaces')}
          />
        }
      >
        <Chips names={names(workspaces, form.workspaceIds)} />
      </ConfigSection>

      <ConfigSection
        icon={Workflow}
        tint="blue"
        title="Workflows"
        count={form.workflowIds.length}
        action={
          <EditButton
            label="Edit workflows"
            disabled={disabled}
            onClick={() => setDialog('workflows')}
          />
        }
      >
        <Chips names={names(workflows, form.workflowIds)} />
      </ConfigSection>

      <ConfigSection icon={Brain} tint="violet" title="Memory">
        <div className="grid gap-2">
          {/* Like a Row, but the switch sizes to its three labels (a Row's value column fits two). */}
          <div className="flex items-center justify-between gap-3">
            <span className="text-muted-foreground text-sm">Status</span>
            <div
              role="radiogroup"
              aria-label="Memory"
              className="bg-muted inline-flex shrink-0 rounded-full p-0.5 text-xs"
            >
              {(['stateless', 'stateful', 'sessions'] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  role="radio"
                  aria-checked={form.memoryMode === mode}
                  disabled={disabled}
                  onClick={() => set('memoryMode')(mode)}
                  className="aria-checked:bg-card aria-checked:text-foreground aria-checked:shadow-panel text-muted-foreground hover:text-foreground focus-visible:ring-ring/50 rounded-full px-3 py-1 font-medium whitespace-nowrap outline-none focus-visible:ring-3 disabled:pointer-events-none"
                >
                  {mode === 'stateless' ? 'Off' : mode === 'stateful' ? 'Thread' : 'Sessions'}
                </button>
              ))}
            </div>
          </div>
          {form.memoryMode === 'stateful' && (
            <Row label="History size" htmlFor="agent-last-messages">
              <Input
                id="agent-last-messages"
                type="number"
                className={SMALL_INPUT}
                value={form.lastMessages}
                disabled={disabled}
                onChange={(e) => set('lastMessages')(e.target.value)}
              />
            </Row>
          )}
          {form.memoryMode === 'sessions' && (
            <div className="grid gap-2">
              <div className="flex items-center gap-2">
                <span
                  className="min-w-0 flex-1 truncate font-mono text-xs"
                  title={levelsSummary(form.sessionLevels)}
                >
                  {form.sessionLevels.length > 0
                    ? levelsSummary(form.sessionLevels)
                    : 'No levels yet'}
                </span>
                <EditButton
                  label="Edit session levels"
                  disabled={disabled}
                  onClick={() => setDialog('sessions')}
                />
              </div>
              {!sessionConfigSchema.safeParse({ levels: form.sessionLevels }).success && (
                <p className="text-destructive text-xs" role="alert">
                  The session levels have problems and are not saved yet.
                </p>
              )}
            </div>
          )}
          <p className="text-muted-foreground text-xs">
            {form.memoryMode === 'stateful'
              ? 'Sends the thread’s most recent messages with each request, when the caller passes the same threadId.'
              : form.memoryMode === 'sessions'
                ? 'Shared notes per level (e.g. global, store, user) and the conversation at the most specific level each request identifies.'
                : 'Every request stands alone.'}
          </p>
        </div>
      </ConfigSection>

      <ConfigSection icon={Gauge} tint="rose" title="Limits">
        <div className="grid gap-2">
          <Row label="Max steps" htmlFor="agent-max-steps">
            <Input
              id="agent-max-steps"
              type="number"
              className={SMALL_INPUT}
              value={form.maxSteps}
              disabled={disabled}
              onChange={(e) => set('maxSteps')(e.target.value)}
            />
          </Row>
          <Row label="Max duration (seconds)" htmlFor="agent-max-duration">
            <Input
              id="agent-max-duration"
              type="number"
              className={SMALL_INPUT}
              value={form.maxDurationSeconds}
              disabled={disabled}
              onChange={(e) => set('maxDurationSeconds')(e.target.value)}
            />
          </Row>
          <Row label="Max output tokens per run" htmlFor="agent-max-output">
            <Input
              id="agent-max-output"
              type="number"
              placeholder="No cap"
              className={SMALL_INPUT}
              value={form.maxOutputTokens}
              disabled={disabled}
              onChange={(e) => set('maxOutputTokens')(e.target.value)}
            />
          </Row>
        </div>
      </ConfigSection>

      <ConfigSection
        icon={History}
        tint="blue"
        title={agent.published ? `Changes since v${agent.published.version}` : 'Not published yet'}
      >
        {agent.published && changes.length === 0 ? (
          <p className="text-muted-foreground text-xs">None. The draft matches the live version.</p>
        ) : (
          <span className="flex flex-wrap gap-1">
            {changes.map((c) => (
              <Badge key={c} variant="outline" className="font-normal">
                {c}
              </Badge>
            ))}
          </span>
        )}
      </ConfigSection>
    </div>
  );

  const tabs = [
    { id: 'chat', label: 'Chat', icon: MessageSquare, className: '' },
    { id: 'config', label: 'Config', icon: Settings2, className: 'lg:hidden' },
    { id: 'versions', label: 'Versions', icon: History, className: '' },
    { id: 'code', label: 'Code', icon: Code2, className: '' },
  ] as const;

  return (
    // Fill the page panel edge to edge, below its bar.
    <div className="-m-4 flex h-[calc(100dvh-3rem)] md:-m-5 md:h-[calc(100dvh-4rem)]">
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex h-11 shrink-0 items-center gap-1 border-b px-2.5">
          <div role="tablist" aria-label="Agent views" className="flex items-center gap-1">
            {tabs.map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={view === t.id}
                onClick={() => setTab(t.id)}
                className={cn(
                  'text-muted-foreground hover:text-foreground aria-selected:bg-accent aria-selected:text-foreground focus-visible:ring-ring/50 inline-flex h-[30px] items-center gap-1.5 rounded-full px-3 text-sm font-medium outline-none focus-visible:ring-3 [&_svg]:size-4',
                  t.className,
                )}
              >
                <t.icon aria-hidden />
                {t.label}
              </button>
            ))}
          </div>
          {view === 'chat' && (
            <div
              role="radiogroup"
              aria-label="Chat mode"
              className="bg-muted ml-auto flex rounded-full p-0.5"
            >
              {(
                [
                  ['build', 'Build', 'Describe the use case; the builder proposes changes'],
                  ['test', 'Test', `Talk to the agent (draft v${agent.draft.version})`],
                ] as const
              ).map(([value, label, hint]) => (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={mode === value}
                  title={hint}
                  onClick={() => setMode(value)}
                  className="text-muted-foreground hover:text-foreground aria-checked:bg-card aria-checked:text-foreground aria-checked:shadow-panel focus-visible:ring-ring/50 h-7 rounded-full px-3 text-xs font-medium outline-none focus-visible:ring-3"
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>
        {save.error && (
          <div className="px-4 pt-3">
            <ErrorAlert error={save.error} />
          </div>
        )}
        {/* Both chats stay mounted so switching modes keeps each conversation. */}
        {view === 'chat' && (
          <>
            <AgentBuilder
              className={cn('flex-1', mode !== 'build' && 'hidden')}
              tenantId={tenantId}
              agentId={agent.id}
              agentName={agent.name}
              draft={{
                systemPrompt: form.systemPrompt,
                provider: provider?.name ?? null,
                model: form.model,
                attached: {
                  tools: names(tools, form.toolIds),
                  mcpServers: names(servers, form.mcpServerIds),
                  skills: names(skills, form.skillIds),
                  workflows: names(workflows, form.workflowIds),
                  claudeBuiltinTools: form.claudeBuiltinTools,
                },
                memoryMode: form.memoryMode,
                lastMessages: form.lastMessages,
                sessionLevels: form.sessionLevels,
                maxSteps: form.maxSteps,
                maxDurationSeconds: form.maxDurationSeconds,
              }}
              available={{
                providers: providerList.map((p) => p.name),
                tools: (tools.data?.items ?? []).map((i) => i.name),
                mcpServers: (servers.data?.items ?? []).map((i) => i.name),
                skills: (skills.data?.items ?? []).map((i) => i.name),
                workflows: (workflows.data?.items ?? []).map((i) => i.name),
                claudeBuiltinTools: builtinsAllowed,
              }}
              onApply={applyChanges}
            />
            <AgentChat
              key={`${agent.id}:${form.memoryMode}`}
              className={cn('flex-1', mode !== 'test' && 'hidden')}
              agentId={agent.id}
              agentName={agent.name}
              version="draft"
              stateful={form.memoryMode !== 'stateless'}
              requestContext={requestContext}
            />
          </>
        )}
        {view === 'config' && <div className="min-h-0 flex-1 overflow-y-auto">{config}</div>}
        {view === 'code' && (
          <div className="min-h-0 flex-1 overflow-y-auto p-5">
            <AgentCode
              endpoint={endpoint}
              expected={expected}
              stateful={(agent.published ?? agent.draft).memoryMode === 'stateful'}
              published={agent.published !== null}
              creating={createEndpoint.isPending}
              onCreateEndpoint={() => createEndpoint.mutate()}
            />
          </div>
        )}
        {view === 'versions' && (
          <div className="min-h-0 flex-1 overflow-y-auto p-5">
            <VersionsList tenantId={tenantId} agent={agent} />
          </div>
        )}
      </div>
      {wide && (
        <aside
          aria-label="Config"
          className="bg-card/60 flex w-[23rem] shrink-0 flex-col border-l xl:w-[25rem]"
        >
          <h2 className="flex h-11 shrink-0 items-center border-b px-4 text-sm font-medium">
            Config
          </h2>
          <div className="min-h-0 flex-1 overflow-y-auto">{config}</div>
        </aside>
      )}

      <Dialog open={dialog === 'prompt'} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="flex max-h-[90dvh] flex-col sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>System prompt</DialogTitle>
            <DialogDescription>
              Instructions the model reads before every conversation. Skills add theirs after this.
              Changes save automatically.
            </DialogDescription>
          </DialogHeader>
          <PromptEditor
            id="system-prompt"
            label="System prompt"
            value={form.systemPrompt}
            onChange={set('systemPrompt')}
            disabled={disabled}
            placeholder="You are a helpful assistant for…"
            textareaRef={promptRef}
            className="h-[min(60vh,36rem)] min-h-64"
            toolbar={
              <InsertPromptMenu tenantId={tenantId} disabled={disabled} onInsert={insertAtCursor} />
            }
            footer={
              variables.length > 0 && (
                <span className="flex flex-wrap items-center gap-1">
                  Filled per request:
                  {variables.map((v) => (
                    <Badge key={v} variant="secondary" className="font-mono font-normal">
                      {v}
                    </Badge>
                  ))}
                </span>
              )
            }
          />
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === 'tools'} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>Tools and MCP servers</DialogTitle>
            <DialogDescription>
              What the model may call while it works. Changes save automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-6 md:grid-cols-2">
            <AttachmentGroup
              tenantId={tenantId}
              label="Tools"
              path="/tools"
              manage="/tools"
              selected={form.toolIds}
              onChange={set('toolIds')}
              disabled={disabled}
            />
            <AttachmentGroup
              tenantId={tenantId}
              label="MCP servers"
              path="/mcp-servers"
              manage="/tools"
              selected={form.mcpServerIds}
              onChange={set('mcpServerIds')}
              disabled={disabled}
            />
          </div>
          <WebSearchToggle
            disabled={disabled}
            checked={form.webSearch}
            onChange={set('webSearch')}
          />
          {provider?.type === 'claude_agent_sdk' && (
            <BuiltinTools
              tenantId={tenantId}
              disabled={disabled}
              selected={form.claudeBuiltinTools}
              onChange={set('claudeBuiltinTools')}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === 'workflows'} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Workflows</DialogTitle>
            <DialogDescription>
              Each workflow becomes a tool the agent can call when a prompt needs it; the workflow’s
              inputs are the tool’s arguments and its description tells the model when to use it.
              Runs show up in the workflow’s history.
            </DialogDescription>
          </DialogHeader>
          <AttachmentGroup
            tenantId={tenantId}
            label="Workflows"
            path="/workflows"
            manage="/workflows"
            selected={form.workflowIds}
            onChange={set('workflowIds')}
            disabled={disabled}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === 'sessions'} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Session levels</DialogTitle>
            <DialogDescription>
              Context the agent keeps across conversations. Changes save automatically.
            </DialogDescription>
          </DialogHeader>
          <SessionLevelsEditor
            levels={form.sessionLevels}
            disabled={disabled}
            onChange={set('sessionLevels')}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === 'skills'} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Skills</DialogTitle>
            <DialogDescription>
              Each skill adds its instructions to the system prompt and its tools to the agent.
            </DialogDescription>
          </DialogHeader>
          <AttachmentGroup
            tenantId={tenantId}
            label="Skills"
            path="/skills"
            manage="/skills"
            selected={form.skillIds}
            onChange={set('skillIds')}
            disabled={disabled}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === 'workspaces'} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Knowledge workspaces</DialogTitle>
            <DialogDescription>
              The agent can search and read the documents in these workspaces with the
              <code>workspace_search</code> and <code>workspace_read</code> tools.
            </DialogDescription>
          </DialogHeader>
          <AttachmentGroup
            tenantId={tenantId}
            label="Workspaces"
            path="/workspaces"
            manage="/workspaces"
            selected={form.workspaceIds}
            onChange={set('workspaceIds')}
            disabled={disabled}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SaveIndicator({ state }: { state: SaveState }) {
  const content = {
    saved: { icon: <Check aria-hidden />, text: 'Saved' },
    pending: { icon: <LoaderCircle aria-hidden className="opacity-50" />, text: 'Unsaved changes' },
    saving: { icon: <LoaderCircle aria-hidden className="animate-spin" />, text: 'Saving…' },
    error: { icon: <CircleAlert aria-hidden />, text: 'Not saved' },
  }[state];
  return (
    <span
      aria-live="polite"
      className={cn(
        'inline-flex items-center gap-1.5 text-xs [&_svg]:size-3.5',
        state === 'error' ? 'text-destructive' : 'text-muted-foreground',
      )}
    >
      {content.icon}
      <span className="sr-only sm:not-sr-only">{content.text}</span>
    </span>
  );
}

/** Jump to another agent from the breadcrumb, like the studio's agent switcher. */
function AgentSwitcher({ tenantId, current }: { tenantId: string; current: string }) {
  const navigate = useNavigate();
  const agents = usePaged<AgentDto>(['agents', tenantId], '/agents');
  const list = agents.data?.pages.flatMap((p) => p.items) ?? [];
  if (list.length < 2) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon-xs" aria-label="Switch agent">
          <ChevronsUpDown aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel>Agents</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {list.map((a) => (
          <DropdownMenuItem
            key={a.id}
            onSelect={() => void navigate({ to: '/agents/$agentId', params: { agentId: a.id } })}
          >
            <span className="flex-1 truncate">{a.name}</span>
            {a.id === current && <Check aria-hidden />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function AgentEditorContent({ tenantId, role }: { tenantId: string; role: Role }) {
  const { agentId } = useParams({ from: '/app/agents/$agentId' });
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const canEdit = hasRole(role, 'admin');
  const [saveState, setSaveState] = useState<SaveState>('saved');
  const [confirm, setConfirm] = useState<'publish' | 'delete' | null>(null);

  const agent = useQuery({
    queryKey: ['agent', tenantId, agentId],
    queryFn: () => api<AgentDetailDto>(`/agents/${agentId}`),
  });

  const onDone = (message: string) => (updated: AgentDetailDto) => {
    toast.success(message);
    queryClient.setQueryData(['agent', tenantId, agentId], updated);
    void queryClient.invalidateQueries({ queryKey: ['agents', tenantId] });
  };
  const publish = useMutation({
    mutationFn: () => api<AgentDetailDto>(`/agents/${agentId}/publish`, { method: 'POST' }),
    onSuccess: (updated) => onDone(`Published v${updated.publishedVersion}`)(updated),
    onError: (error) => toast.error(errorMessage(error)),
  });
  const archive = useMutation({
    mutationFn: (archived: boolean) =>
      api<AgentDetailDto>(`/agents/${agentId}/${archived ? 'archive' : 'restore'}`, {
        method: 'POST',
      }),
    onSuccess: (updated) =>
      onDone(updated.status === 'archived' ? 'Agent archived' : 'Agent restored')(updated),
    onError: (error) => toast.error(errorMessage(error)),
  });
  const remove = useMutation({
    mutationFn: () => api<void>(`/agents/${agentId}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success('Agent deleted');
      void queryClient.invalidateQueries({ queryKey: ['agents', tenantId] });
      void navigate({ to: '/agents' });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  if (!agent.data) return <ErrorAlert error={agent.error} />;
  const a = agent.data;
  const changes = changedFields(a.published, a.draft);
  const unchanged = a.published !== null && changes.length === 0;
  const publishBlocked =
    saveState !== 'saved'
      ? 'Wait for your changes to save'
      : unchanged
        ? 'The draft has no changes since the live version'
        : undefined;

  return (
    <>
      <PageHeader
        title={a.name}
        breadcrumb={[{ label: 'Agents', to: '/agents' }]}
        titleAddon={
          <>
            <AgentSwitcher tenantId={tenantId} current={a.id} />
            <Badge variant={STATUS_VARIANT[a.status]} className="hidden sm:inline-flex">
              {AGENT_STATUS_LABELS[a.status]}
            </Badge>
          </>
        }
        actions={
          <>
            {canEdit && <SaveIndicator state={saveState} />}
            {canEdit && (
              <>
                <Button
                  size="sm"
                  onClick={() => setConfirm('publish')}
                  disabled={publishBlocked !== undefined || publish.isPending}
                  title={publishBlocked}
                >
                  {publish.isPending ? 'Publishing…' : `Publish v${a.draft.version}`}
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon-sm" aria-label="More actions">
                      <Ellipsis aria-hidden />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem asChild>
                      <Link to="/playground" search={{ agentId: a.id }}>
                        <MessageSquare aria-hidden />
                        Open in Playground
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => archive.mutate(a.status !== 'archived')}>
                      {a.status === 'archived' ? (
                        <ArchiveRestore aria-hidden />
                      ) : (
                        <Archive aria-hidden />
                      )}
                      {a.status === 'archived' ? 'Restore agent' : 'Archive agent'}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem variant="destructive" onSelect={() => setConfirm('delete')}>
                      <Trash2 aria-hidden />
                      Delete agent
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
          </>
        }
      />

      <DraftWorkspace
        key={a.draft.id}
        tenantId={tenantId}
        agent={a}
        canEdit={canEdit}
        onSaveState={setSaveState}
      />

      <AlertDialog open={confirm !== null} onOpenChange={(open) => !open && setConfirm(null)}>
        <AlertDialogContent size={confirm === 'publish' ? 'lg' : 'default'}>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm === 'publish' ? `Publish v${a.draft.version}?` : `Delete ${a.name}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm === 'publish'
                ? 'Clients start using this version immediately. It cannot be edited afterwards; you keep working on a new draft.'
                : 'The agent, all of its versions and its run history are removed permanently.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {confirm === 'publish' && (
            <div className="grid min-w-0 gap-4">
              <div className="grid gap-1.5">
                <span className="text-sm font-medium">What changes</span>
                <span className="flex flex-wrap gap-1">
                  {changes.map((c) => (
                    <Badge key={c} variant="outline" className="font-normal">
                      {c}
                    </Badge>
                  ))}
                </span>
              </div>
              {a.draft.systemPrompt !== (a.published?.systemPrompt ?? '') && (
                <PromptDiff before={a.published?.systemPrompt ?? ''} after={a.draft.systemPrompt} />
              )}
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant={confirm === 'delete' ? 'danger' : 'default'}
              onClick={() => (confirm === 'publish' ? publish.mutate() : remove.mutate())}
            >
              {confirm === 'publish' ? `Publish v${a.draft.version}` : 'Delete agent'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export function AgentEditorPage() {
  return (
    <RequireTenant>
      {(tenant) => <AgentEditorContent tenantId={tenant.id} role={tenant.role} />}
    </RequireTenant>
  );
}
